import React from "react";
import PolicyLayout, { Section } from "@/components/PolicyLayout";

const SECTIONS = [
  { id: "intro", label: "1. Introduction" },
  { id: "definitions", label: "2. Definitions" },
  { id: "eligibility", label: "3. Eligibility" },
  { id: "account", label: "4. Account & Security" },
  { id: "role", label: "5. Role of Florvanta" },
  { id: "vendor-terms", label: "6. Vendor Terms" },
  { id: "buyer-terms", label: "7. Buyer Terms" },
  { id: "listings", label: "8. Product Listings" },
  { id: "pricing", label: "9. Pricing & Taxes (GST)" },
  { id: "commission", label: "10. Commission & Settlements" },
  { id: "payments", label: "11. Payments (RBI-Compliant)" },
  { id: "shipping", label: "12. Shipping & Delivery" },
  { id: "returns", label: "13. Returns, Refunds & Cancellations" },
  { id: "prohibited", label: "14. Prohibited Conduct" },
  { id: "ip", label: "15. Intellectual Property" },
  { id: "privacy", label: "16. Privacy & Data" },
  { id: "liability", label: "17. Limitation of Liability" },
  { id: "indemnity", label: "18. Indemnity" },
  { id: "termination", label: "19. Suspension & Termination" },
  { id: "law", label: "20. Governing Law & Jurisdiction" },
  { id: "dispute", label: "21. Dispute Resolution" },
  { id: "grievance", label: "22. Grievance Officer" },
  { id: "changes", label: "23. Changes to Terms" },
  { id: "contact", label: "24. Contact" },
];

export default function Terms() {
  return (
    <PolicyLayout
      title="Terms & Conditions"
      tagline="These Terms govern your use of the Florvanta Commerce marketplace, operated by Florvanta Commerce Pvt Ltd. By accessing or using the Platform, you agree to be bound by these Terms."
      updated="February 2026"
      sections={SECTIONS}
    >
      <Section id="intro" title="1. Introduction">
        <p>
          These Terms &amp; Conditions (&quot;<b>Terms</b>&quot;) form a legally binding agreement between you (&quot;<b>User</b>&quot;, &quot;<b>Buyer</b>&quot;, or &quot;<b>Vendor</b>&quot;) and <b>Florvanta Commerce Pvt Ltd</b> (&quot;<b>Florvanta</b>&quot;, &quot;<b>we</b>&quot;, &quot;<b>us</b>&quot; or &quot;<b>our</b>&quot;), a company incorporated in India under the Companies Act, 2013.
        </p>
        <p>
          These Terms are published in accordance with Rule 3(1)(a) of the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021 (&quot;<b>IT Rules 2021</b>&quot;) and Section 5 of the Consumer Protection (E-Commerce) Rules, 2020. This is an electronic record and does not require any physical or digital signature.
        </p>
        <p>
          By clicking &quot;I Agree&quot;, registering, or otherwise using the Platform, you acknowledge that you have read, understood and agreed to these Terms, the Privacy Policy, the Return Policy, the Refund Policy, the Shipping Policy, and all other policies referenced herein.
        </p>
      </Section>

      <Section id="definitions" title="2. Definitions">
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Platform</b> means the Florvanta website, mobile applications, and any related services offered by Florvanta Commerce Pvt Ltd.</li>
          <li><b>Buyer</b> means any registered user purchasing products through the Platform.</li>
          <li><b>Vendor / Seller</b> means any registered business or individual listing and selling products on the Platform.</li>
          <li><b>Order</b> means a request placed by a Buyer through the Platform for the purchase of one or more products.</li>
          <li><b>Commission</b> means the fee, currently one percent (1%) of the sale value (excluding GST) per successful transaction, retained by Florvanta.</li>
          <li><b>Payment Aggregator (PA)</b> means an RBI-authorised entity engaged by Florvanta to facilitate collection and disbursement of funds under the RBI Guidelines on Regulation of Payment Aggregators and Payment Gateways dated 17 March 2020 (as amended).</li>
        </ul>
      </Section>

      <Section id="eligibility" title="3. Eligibility">
        <p>
          You must be at least eighteen (18) years of age and competent to contract under the Indian Contract Act, 1872 to use the Platform. If you are registering as a Vendor, you additionally represent that you are duly authorised to bind the business entity you claim to represent.
        </p>
      </Section>

      <Section id="account" title="4. Account &amp; Security">
        <p>
          You are responsible for maintaining the confidentiality of your login credentials and for all activities conducted under your account. Florvanta shall not be liable for any loss arising from unauthorised use of your account. You must immediately notify us at <b>grievance@florvanta.co.in</b> upon becoming aware of any unauthorised use.
        </p>
      </Section>

      <Section id="role" title="5. Role of Florvanta as an Intermediary">
        <p>
          Florvanta operates as an &quot;<b>intermediary</b>&quot; under Section 2(1)(w) of the Information Technology Act, 2000 and as a &quot;<b>marketplace e-commerce entity</b>&quot; under Rule 3 of the Consumer Protection (E-Commerce) Rules, 2020. Florvanta does not directly sell or own any inventory listed by Vendors on the Platform.
        </p>
        <p>
          Florvanta&apos;s role is limited to (i) providing the technology platform, (ii) collecting payments through an RBI-authorised Payment Aggregator, (iii) retaining commission, and (iv) remitting the balance to Vendors after applicable deductions. Florvanta is not the manufacturer, importer, seller, or distributor of any product listed on the Platform.
        </p>
      </Section>

      <Section id="vendor-terms" title="6. Vendor Terms">
        <ul className="list-disc pl-5 space-y-1">
          <li>Vendors must complete Know-Your-Customer (KYC) verification, including submission of PAN, GSTIN (where applicable), bank account, address proof, and business incorporation documents.</li>
          <li>Vendors are solely responsible for the accuracy, completeness, and legality of product listings, pricing, descriptions, images, and stock availability.</li>
          <li>Vendors warrant that they possess all necessary licenses, registrations, and permits required to sell the listed products under applicable Indian law (including BIS, FSSAI, Drugs and Cosmetics Act, Legal Metrology Act, etc., as applicable).</li>
          <li>Vendors must fulfil orders within the shipping timelines committed and must not sell counterfeit, expired, hazardous, or restricted products.</li>
          <li>Vendors bear complete responsibility for after-sales support, warranty, and any defects in the products sold.</li>
        </ul>
      </Section>

      <Section id="buyer-terms" title="7. Buyer Terms">
        <ul className="list-disc pl-5 space-y-1">
          <li>Buyers must provide accurate delivery address, contact number, and payment information.</li>
          <li>Buyers must inspect products upon delivery and raise any concerns within the timelines set out in our Return Policy.</li>
          <li>Buyers agree not to place fraudulent orders, mis-use Cash on Delivery, or engage in refund abuse.</li>
        </ul>
      </Section>

      <Section id="listings" title="8. Product Listings">
        <p>
          Product listings, including images, descriptions, specifications, and MRP, are provided by Vendors. Florvanta makes reasonable efforts to review listings for compliance but does not warrant the accuracy or completeness of any listing. In the event of a discrepancy between the displayed information and the physical product, Buyers may raise a complaint under the Return / Refund Policy.
        </p>
      </Section>

      <Section id="pricing" title="9. Pricing, Taxes &amp; GST">
        <p>
          All prices displayed on the Platform are in Indian Rupees (INR) and are inclusive of applicable Goods and Services Tax (GST) unless expressly stated otherwise. Vendors are responsible for issuing GST-compliant tax invoices to Buyers under the Central Goods and Services Tax Act, 2017 and the applicable State GST Acts. Florvanta may collect and deposit Tax Collected at Source (TCS) under Section 52 of the CGST Act, 2017 where applicable.
        </p>
      </Section>

      <Section id="commission" title="10. Commission &amp; Settlements">
        <p>
          Florvanta charges a flat commission of <b>1% (one percent) of the net sale value</b> (i.e., sale value excluding GST, shipping charges, and any statutory levies) per successful transaction. Commission is deducted at source before disbursement of settlement amounts to Vendors.
        </p>
        <p>
          Settlement to Vendors shall be initiated on a T+7 working day basis from the date of successful delivery and expiry of the return window, in line with the RBI Guidelines on Nodal / Escrow accounts and Payment Aggregator settlement timelines. Settlement is subject to (i) no active dispute on the order, (ii) valid GSTIN or PAN on file, and (iii) reconciliation with the Payment Aggregator.
        </p>
      </Section>

      <Section id="payments" title="11. Payments &amp; RBI Compliance">
        <p>
          All online payments on the Platform are processed by an RBI-authorised Payment Aggregator in accordance with the <b>RBI Guidelines on Regulation of Payment Aggregators and Payment Gateways</b> dated 17 March 2020 (as amended from time to time). Buyer funds are held in an escrow / nodal account maintained by the Payment Aggregator with a scheduled commercial bank and are not co-mingled with the working capital of Florvanta.
        </p>
        <p>
          Florvanta does <b>not</b> store any card numbers, CVV, UPI PIN, net-banking passwords, or full payment credentials on its own systems. All payment data is tokenised and stored by the Payment Aggregator in accordance with the RBI Card-on-File Tokenisation framework and Payment Card Industry Data Security Standard (PCI-DSS).
        </p>
        <p>
          Cash on Delivery (COD) is offered subject to serviceability and may be withdrawn without notice in accordance with the RBI framework on prepaid instruments and the Consumer Protection (E-Commerce) Rules, 2020.
        </p>
        <p>
          <b>Chargebacks &amp; unauthorised transactions:</b> Buyers may report unauthorised or disputed electronic transactions in accordance with the <b>RBI Circular on Limiting Liability of Customers in Unauthorised Electronic Banking Transactions</b> (DBR.No.Leg.BC.78/09.07.005/2017-18 dated 6 July 2017) by writing to <b>grievance@florvanta.co.in</b> within three (3) working days.
        </p>
      </Section>

      <Section id="shipping" title="12. Shipping &amp; Delivery">
        <p>Please refer to the <a className="text-[#14A085] underline" href="/shipping-policy">Shipping Policy</a> for detailed information on shipping timelines, serviceable pin-codes, tracking, and delivery.</p>
      </Section>

      <Section id="returns" title="13. Returns, Refunds &amp; Cancellations">
        <p>Please refer to our <a className="text-[#14A085] underline" href="/return-policy">Return Policy</a> and <a className="text-[#14A085] underline" href="/refund-policy">Refund Policy</a>.</p>
      </Section>

      <Section id="prohibited" title="14. Prohibited Conduct">
        <p>Users shall not:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Post any content that is unlawful, obscene, defamatory, or violates any third-party rights, in contravention of Rule 3(1)(b) of the IT Rules 2021.</li>
          <li>Engage in phishing, spoofing, scraping, or unauthorised access to the Platform.</li>
          <li>List prohibited goods (narcotics, wildlife, hazardous chemicals, replicas, counterfeit goods, obscene material, weapons, etc.).</li>
          <li>Manipulate reviews, ratings, or search rankings.</li>
          <li>Attempt to circumvent the Platform to transact off-platform.</li>
        </ul>
      </Section>

      <Section id="ip" title="15. Intellectual Property">
        <p>
          All trademarks, logos, service marks, and content on the Platform, other than Vendor-supplied product content, are the exclusive property of Florvanta Commerce Pvt Ltd or its licensors. Unauthorised use is strictly prohibited.
        </p>
      </Section>

      <Section id="privacy" title="16. Privacy &amp; Data Protection">
        <p>
          Your personal data is processed in accordance with our <a className="text-[#14A085] underline" href="/privacy">Privacy Policy</a>, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and the Digital Personal Data Protection Act, 2023 (as and when notified).
        </p>
      </Section>

      <Section id="liability" title="17. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, Florvanta&apos;s aggregate liability arising out of or relating to a transaction shall not exceed the amount of Commission actually received by Florvanta on such transaction. Florvanta shall not be liable for any indirect, incidental, consequential, or punitive damages.
        </p>
      </Section>

      <Section id="indemnity" title="18. Indemnity">
        <p>
          You agree to indemnify and hold harmless Florvanta, its officers, directors, employees, and agents from any claims, damages, or expenses arising from (i) your breach of these Terms, (ii) violation of applicable law, or (iii) infringement of third-party rights.
        </p>
      </Section>

      <Section id="termination" title="19. Suspension &amp; Termination">
        <p>
          Florvanta reserves the right, at its sole discretion, to suspend, restrict, or terminate any account for violation of these Terms, applicable law, or upon receipt of a valid legal request from a governmental authority.
        </p>
      </Section>

      <Section id="law" title="20. Governing Law &amp; Jurisdiction">
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the Republic of India. Subject to the arbitration clause below, the courts at <b>Howrah, West Bengal</b> shall have exclusive jurisdiction.
        </p>
      </Section>

      <Section id="dispute" title="21. Dispute Resolution &amp; Arbitration">
        <p>
          Any dispute, controversy, or claim arising out of or relating to these Terms shall first be attempted to be resolved amicably within thirty (30) days. Failing amicable resolution, the dispute shall be referred to arbitration by a sole arbitrator appointed by Florvanta, conducted under the Arbitration and Conciliation Act, 1996. The seat and venue of arbitration shall be <b>Howrah, West Bengal</b>, India. The language shall be English.
        </p>
      </Section>

      <Section id="grievance" title="22. Grievance Officer">
        <p>
          In accordance with Rule 3(2) of the IT Rules 2021 and Rule 5(3) of the Consumer Protection (E-Commerce) Rules, 2020, the Grievance Officer of Florvanta is:
        </p>
        <div className="rounded-lg border border-[#E2E8E5] bg-white p-4 text-[#0F3D2E]">
          <div><b>Name:</b> Mr. Debasis Saibo, Grievance Officer</div>
          <div><b>Company:</b> Florvanta Commerce Private Limited</div>
          <div><b>Email:</b> info@florvanta.co.in</div>
          <div><b>Phone:</b> +91 91474 25375</div>
          <div><b>Address:</b> 63, Sri Arabinda Road, Salkia, Howrah, West Bengal - 711106</div>
          <div><b>Response TAT:</b> Acknowledgement within 48 hours; resolution within 15 days</div>
        </div>
      </Section>

      <Section id="changes" title="23. Changes to Terms">
        <p>
          Florvanta may modify these Terms at any time. Material changes shall be notified via email or on-Platform notice at least seven (7) days before the effective date. Continued use of the Platform after the effective date shall constitute acceptance.
        </p>
      </Section>

      <Section id="contact" title="24. Contact Us">
        <p>
          Florvanta Commerce Private Limited · <b>info@florvanta.co.in</b> · <b>+91 91474 25375</b>
        </p>
      </Section>
    </PolicyLayout>
  );
}
